import { useCloseModal } from '@/atoms'
import {
  Dispatch,
  type PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from 'react'
import { ModalPublicProps } from '../types'

type CollectTargetRangeContext = ModalPublicProps['collectTargetRange']

const collectTargetRangeContext = createContext<CollectTargetRangeContext>(() => {
  throw new Error('setTargetRange not implemented')
})
const targetRangeContext = createContext<uui.domain.ui.forms.TargetRange>('ORDER')
const targetRangeSetterContext = createContext<
  Dispatch<SetStateAction<uui.domain.ui.forms.TargetRange>>
>(() => undefined)
const cancelContext = createContext<() => void>(() => undefined)
const allowedTargetRangesContext =
  createContext<uui.domain.ui.forms.RecurrenceCollectedTargetRange>({
    ORDER: { visible: true, disabled: false },
    FUTURE: { visible: true, disabled: false },
    ALL: { visible: true, disabled: false },
  })

export function ContextProvider(props: PropsWithChildren<ModalPublicProps>) {
  const { children, allowedTargetRanges, collectTargetRange, onCancel } = props

  const [targetRange, setLocalTargetRange] = useState(() => {
    for (const targetRange in allowedTargetRanges) {
      if (allowedTargetRanges[targetRange].visible && !allowedTargetRanges[targetRange].disabled) {
        return targetRange as uui.domain.ui.forms.TargetRange
      }
    }

    return 'ORDER' as uui.domain.ui.forms.TargetRange
  })

  return (
    <allowedTargetRangesContext.Provider value={allowedTargetRanges}>
      <collectTargetRangeContext.Provider value={collectTargetRange}>
        <targetRangeContext.Provider value={targetRange}>
          <targetRangeSetterContext.Provider value={setLocalTargetRange}>
            <cancelContext.Provider value={onCancel}>{children}</cancelContext.Provider>
          </targetRangeSetterContext.Provider>
        </targetRangeContext.Provider>
      </collectTargetRangeContext.Provider>
    </allowedTargetRangesContext.Provider>
  )
}

export function useOnSave() {
  const close = useCloseModal()
  const collectTargetRange = useContext(collectTargetRangeContext)
  const targetRange = useContext(targetRangeContext)
  return () => {
    collectTargetRange(targetRange)
    close()
  }
}

export function useOnChangeTargetRange() {
  const setTargetRange = useContext(targetRangeSetterContext)
  return (newTargetRange: uui.domain.ui.forms.TargetRange) => setTargetRange(newTargetRange)
}

export function useTargetRange() {
  return useContext(targetRangeContext)
}

export function useOnCancel() {
  const close = useCloseModal()
  const onCancel = useContext(cancelContext)
  return () => {
    onCancel()
    close()
  }
}

export function useAllowedTargetRanges() {
  return useContext(allowedTargetRangesContext)
}
